import { useMemo } from 'react'

import type { NavItem } from './menuItems'

import type { Account, UIFeatures } from '@models/Account'
import { isAdminUser } from '@helpers/isAdminUser'

// Narrow shapes that capture exactly what filterNavItems reads. Used
// in the function signature below so test fixtures can build minimal
// objects without `as unknown as Account` casts. Production callers
// pass full Account / NavItem[]; those satisfy the Pick types
// structurally. `team` is narrowed further to just the one nested
// field the filter reads (`settings.orgstructure_visibility`), so
// the test fixture can pass `{ settings: {} }` without an explicit
// cast — the full AccountTeam shape is irrelevant to filtering.
export type NavFilterUser = Pick<Account, 'role' | 'permissions' | 'roles' | 'role_info'> & {
  team: { settings?: { orgstructure_visibility?: string | null; ui_features?: UIFeatures } } | null
}
export type NavFilterItem = Pick<
  NavItem,
  'to' | 'label' | 'show' | 'requirePerm' | 'requireAnyPerm' | 'roleVisibilityPermission' | 'uiFeatureKey'
>

export const useWindowControlsOverlay = () => {
  return useMemo(() => {
    return navigator.windowControlsOverlay !== undefined && navigator.windowControlsOverlay.visible
  }, [navigator.windowControlsOverlay?.visible])
}

// Admin check for the non-render path (filterNavItems runs outside React).
// Delegates to the shared `isAdminUser` pure helper — single source of
// truth with useIsAdmin() so the two can't drift during the RBAC migration.
const userIsAdmin = (user?: NavFilterUser): boolean => isAdminUser(user)

/**
 * Resolve a user's effective permission set from /me. Mirrors what
 * `useHasPermission` does — duplicated here as a pure function so
 * filterNavItems can be called outside a React render context (e.g.
 * during memoization with stale closures).
 */
const userPermissions = (user?: NavFilterUser): Set<string> => {
  return new Set(user?.permissions ?? [])
}

export const filterNavItems = <T extends NavFilterItem>(
  items: T[],
  user?: NavFilterUser,
  showNavsWithRestriction?: Record<string, boolean>,
): T[] => {
  const isAdmin = userIsAdmin(user)
  // The setting stores the literal string "admin" (per backend default).
  // It controls whether a workspace restricts Team menu to admins only —
  // string comparison is intentional and matches the backend contract.
  const canViewAdminStructure = user?.team?.settings?.orgstructure_visibility === 'admin'
  const uiFeatures = user?.team?.settings?.ui_features
  const perms = userPermissions(user)

  return items.filter((item) => {
    const isAllowed =
      item.to && showNavsWithRestriction?.[item.to] !== undefined ? showNavsWithRestriction[item.to] : true

    // Workspace feature toggle (Settings → Features) — checked FIRST, before
    // any RBAC gate: a feature the workspace hid for the viewer's axis
    // (admin/user) never shows even when perms would allow it; an enabled
    // (or unset) one falls through to the perm checks below. `user`
    // undefined only pre-AuthRoute, which blocks render until /me resolves.
    if (uiFeatures && item.uiFeatureKey) {
      const featureSetting = uiFeatures[item.uiFeatureKey]
      if (featureSetting) {
        const isFeatureVisible = isAdmin ? featureSetting.admin : featureSetting.user
        if (!isFeatureVisible) {
          return false
        }
      }
    }

    if (item.roleVisibilityPermission) {
      // Composes with `requirePerm`: a member with team.view + workspace
      // orgstructure_visibility=admin still doesn't see Team. The check
      // is "if workspace restricts to admin, only admin sees it; else
      // anyone passing the perm check sees it."
      const passesRoleVisibility = canViewAdminStructure ? isAdmin : true
      if (!passesRoleVisibility) return false
    }

    // RBAC permission codename. When set, the user must hold the
    // codename via any of their roles. Falls through to the legacy
    // `show` check if `requirePerm` is unset so non-permission nav
    // items (Dashboard, AI Assistant, Approval Requests, etc.) still
    // work via the legacy role gate.
    // Container entries reachable via several granular perms: visible if
    // the user holds ANY of them. Checked before `requirePerm` so it
    // takes precedence when both are (mistakenly) set.
    if (item.requireAnyPerm !== undefined) {
      return isAllowed && item.requireAnyPerm.some((p) => perms.has(p))
    }

    if (item.requirePerm !== undefined) {
      return isAllowed && perms.has(item.requirePerm)
    }

    return isAllowed && (!item.show || (user && item.show.includes(user.role)))
  })
}
